import Splide from '@splidejs/splide';
import { initSliderControls } from './slider-controls';

// Focusable elements Splide manages inside slides. Links hidden from
// assistive tech (e.g. duplicate image links with tabindex="-1") are left
// alone so Splide doesn't make them tabbable.
const FOCUSABLE_NODES = 'a:not([aria-hidden="true"]), button, input, select';

// Turns plain markup into a Splide slider only while `query` matches, and
// restores the markup otherwise. The Splide classes are added on mount and
// removed on destroy, so CSS can style the non-slider layout freely.
// When the slides fit without overflowing, the slider doesn't loop, drag or
// autoplay and the progress bar stays hidden.
//
// Returns `sync()` (mount, remount or destroy for the current viewport) and
// `destroy()`.
export const createResponsiveSlider = ({
  section,
  root,
  track,
  list,
  slides,
  query,
  progress,
  progressBar,
  progressThumb,
  options = {},
  interval = 5000,
  speed = 700,
  getSlideLabel,
}) => {
  const mediaQuery = window.matchMedia(query);
  let slider = null;
  let controls = null;
  let mountedCanLoop = null;

  const hasOverflow = () => {
    const slidesWidth = slides.reduce(
      (width, slide) => width + slide.getBoundingClientRect().width,
      0
    );

    return slides.length > 1 && slidesWidth > track.clientWidth + 1;
  };

  const setSplideClasses = (enabled) => {
    root.classList.toggle('splide', enabled);
    track.classList.toggle('splide__track', enabled);
    list.classList.toggle('splide__list', enabled);
    slides.forEach((slide) => slide.classList.toggle('splide__slide', enabled));
  };

  const destroy = () => {
    if (!slider) return;

    controls?.destroy();
    slider.destroy(true);
    slider = null;
    controls = null;
    mountedCanLoop = null;
    setSplideClasses(false);
    track.removeAttribute('style');
    list.removeAttribute('style');
    slides.forEach((slide) => {
      slide.removeAttribute('style');
      slide
        .querySelectorAll(FOCUSABLE_NODES)
        .forEach((node) => node.removeAttribute('tabindex'));
    });
    progress?.classList.add('is-disabled');
  };

  const mount = () => {
    destroy();

    const canLoop = hasOverflow();

    setSplideClasses(true);
    progress?.classList.toggle('is-disabled', !canLoop);
    slider = new Splide(root, {
      arrows: false,
      autoWidth: true,
      drag: canLoop,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      focusableNodes: FOCUSABLE_NODES,
      keyboard: 'focused',
      pagination: false,
      perMove: 1,
      type: canLoop ? 'loop' : 'slide',
      ...options,
    });
    controls = initSliderControls({
      section,
      slider,
      slides,
      progressBar,
      progressThumb,
      interval,
      speed,
      loop: canLoop,
      getSlideLabel,
    });
    slider.mount();
    mountedCanLoop = canLoop;
  };

  const sync = () => {
    if (!mediaQuery.matches) {
      destroy();
      return;
    }

    if (!slider || mountedCanLoop !== hasOverflow()) mount();
  };

  return { sync, destroy };
};
